import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Projects', path: '/portfolio' },
    { name: 'AI Twin', path: '/ai-twin' },
    { name: 'Contact', path: '/contact' }
  ];

  // Logo component - wrapped in H1 only on homepage for SEO
  const LogoContent = (
    <Link to="/" className="flex items-center gap-3 group relative z-50">
      <img 
        src="https://static.readdy.ai/image/0b415033dc3c77175cdee172dc914215/77d9c3715369a42bfee09ef46b96ab56.png" 
        alt="Giedrius Petrauskas - Developer & Product Creator" 
        className="h-10 object-contain transition-transform duration-300 group-hover:scale-110"
      />
    </Link>
  );

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-[1060px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo - H1 only on homepage for SEO */}
            {isHomePage ? (
              <h1 className="m-0 p-0 text-base font-normal">{LogoContent}</h1>
            ) : (
              LogoContent
            )}

            {/* Navigation Links - Desktop */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-xs font-medium tracking-[0.1em] transition-colors duration-300 relative group ${
                    location.pathname === link.path
                      ? 'text-[#1B4332]'
                      : 'text-[#2D3436] hover:text-[#1B4332]'
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#1B4332] transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
              ))}
            </div>

            {/* CTA Button - Desktop */}
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-2 bg-[#1B4332] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#0f2419] transition-all duration-300 hover:shadow-lg group whitespace-nowrap"
            >
              <span>Start a Conversation</span>
              <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
            </Link>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center relative z-50"
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`w-full h-0.5 bg-[#2D3436] transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                }`}></span>
                <span className={`w-full h-0.5 bg-[#2D3436] transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}></span>
                <span className={`w-full h-0.5 bg-[#2D3436] transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}></span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden ${
        isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`} onClick={() => setIsMobileMenuOpen(false)}></div>

      {/* Mobile Menu */}
      <div className={`fixed top-0 right-0 bottom-0 w-[280px] bg-white shadow-2xl z-40 transition-transform duration-300 md:hidden ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full pt-28 px-8">
          {/* Navigation Links */}
          <nav className="flex flex-col gap-6">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-lg font-medium transition-all duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#1B4332] translate-x-2'
                    : 'text-[#2D3436] hover:text-[#1B4332] hover:translate-x-2'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center justify-center gap-2 bg-[#1B4332] text-white px-6 py-4 rounded-full text-sm font-medium hover:bg-[#0f2419] transition-all duration-300 whitespace-nowrap"
          >
            <span>Start a Conversation</span>
            <i className="ri-arrow-right-line text-base"></i>
          </Link>

          {/* Decorative element */}
          <div className="mt-auto mb-8 pt-8 border-t border-gray-200">
            <p className="text-xs text-gray-400 text-center">
              © 2025 Giedrius Petrauskas
            </p>
          </div>
        </div>
      </div>
    </>
  );
}